import { Grid, MenuItem } from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";
import { phoneRegex, shirtSizeOptions } from "../../../constants";
import { t } from "../../../i18n/util";
import { IGetApplicantDetailsResponse } from "../../../types/models";
import { DetailsGrid } from "../../application/DetailsGrid";
import { PersonalDataGrid } from "../../application/PersonalDataGrid";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { CustomInputField } from "../../ui/CustomInputField";
import { DialogHeader, ErrorMessagesList, FormButtons, HrBotDialog, SectionHeaderSmall } from "../../ui/Primitives";
import { YupLocalizedNumber } from "../../util/helpers";

type Props = {
    initialValues: IGetApplicantDetailsResponse;
    onSubmit: (model: IGetApplicantDetailsResponse) => void;
    onBack?: () => void;
    isOpen: boolean;
};

export const ApplicantDetailsEditDialog = ({ initialValues, onSubmit, onBack, isOpen }: Props) => {
    const [open, setOpen] = React.useState(isOpen);
    const isMobile = useMobileMediaQuery();

    React.useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const handleClose = () => {
        setOpen(false);
        onBack?.();
    };

    const handleSubmit = (model: IGetApplicantDetailsResponse) => {
        setOpen(false);
        onSubmit(model);
    };

    return (
        <HrBotDialog open={open} maxWidth={isMobile ? undefined : false}>
            <div style={{ maxWidth: 1000 }}>
                <DialogHeader label={t("screen.applicantDetails.edit")} onClose={handleClose} />
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={Yup.object().shape({
                        firstName: Yup.string().required(t("error.required")),
                        lastName: Yup.string().required(t("error.required")),
                        jobCategory: Yup.string().required(t("error.required")),
                        phone: Yup.string().required(t("error.required")).matches(phoneRegex, t("error.phone")),
                        email: Yup.string().required(t("error.required")).email(t("error.email")).trim(),
                        previousProjects: Yup.string().url(t("error.website")),
                        discovery: Yup.string().required(t("error.required")),
                        personnelNumber: Yup.string(),
                        shirtSize: Yup.string(),
                        salary: YupLocalizedNumber().min(0, t("error.salary")).required(t("error.required")),
                        experience: Yup.number(),
                        startDate: Yup.date().nullable().required(t("error.required")).typeError(t("error.date")),
                        noticePeriod: YupLocalizedNumber().min(0, t("error.noticePeriod")).nullable(),
                        workHours: YupLocalizedNumber().min(0, t("error.workHours")).required(t("error.required")),
                        language: Yup.string().required(t("error.required")),
                        device: Yup.string().required(t("error.required")),
                        deviceSN: Yup.string()
                    })}
                    validateOnChange
                    validateOnBlur
                >
                    {({ setFieldValue, errors, touched, values }) => {
                        const showDifferentDiscoverySourceField = values.discovery === "different_source";

                        return (
                            <Form>
                                <div style={{ padding: 24 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            height: "100%"
                                        }}
                                    >
                                        <div>
                                            <SectionHeaderSmall
                                                label={t("screen.personalData.title")}
                                                style={{ marginBottom: 16 }}
                                            />
                                            <PersonalDataGrid
                                                errors={errors}
                                                touched={touched}
                                                lastFieldMd={showDifferentDiscoverySourceField ? 6 : undefined}
                                                lastFieldLg={showDifferentDiscoverySourceField ? 6 : undefined}
                                                customDiscoverySourceMd={6}
                                                customDiscoverySourceLg={6}
                                                showCustomDiscoverySourceField={showDifferentDiscoverySourceField}
                                            >
                                                <Grid item xs={12} md={6} lg={6}>
                                                    <Field
                                                        component={CustomInputField}
                                                        label={t("screen.applicantDetails.personnelNumber")}
                                                        name="personnelNumber"
                                                        type="text"
                                                        errorMessage={errors.personnelNumber}
                                                        isTouched={touched.personnelNumber}
                                                        optional
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6} lg={6}>
                                                    <Field
                                                        component={CustomInputField}
                                                        label={t("screen.applicantDetails.shirtSize")}
                                                        name="shirtSize"
                                                        errorMessage={errors.shirtSize}
                                                        isTouched={touched.shirtSize}
                                                        select
                                                        required
                                                    >
                                                        {shirtSizeOptions.map((option) => (
                                                            <MenuItem key={uuidv4()} value={option.value}>
                                                                {t(option.messageId)}
                                                            </MenuItem>
                                                        ))}
                                                    </Field>
                                                </Grid>
                                            </PersonalDataGrid>
                                        </div>
                                        <div>
                                            <SectionHeaderSmall
                                                label={t("screen.details.title")}
                                                style={{ marginBottom: 16 }}
                                            />
                                            <DetailsGrid
                                                errors={errors}
                                                touched={touched}
                                                setFieldValue={setFieldValue}
                                                lastFieldMd={6}
                                            >
                                                <Grid item sm={12} md={6}>
                                                    <Field
                                                        component={CustomInputField}
                                                        label={t("screen.applicantDetails.deviceSN")}
                                                        name="deviceSN"
                                                        type="text"
                                                        errorMessage={errors.deviceSN}
                                                        isTouched={touched.deviceSN}
                                                        optional
                                                    />
                                                </Grid>
                                            </DetailsGrid>
                                        </div>
                                        <ErrorMessagesList errors={errors} touched={touched} />
                                        <FormButtons
                                            labelBack={t("screen.dataUpload.dialog.cancel")}
                                            labelSubmit={t("screen.applicantDetails.save")}
                                            onBack={handleClose}
                                            style={{
                                                justifyContent: "flex-end",
                                                marginBottom: 0
                                            }}
                                        />
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </HrBotDialog>
    );
};
